import React, {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react'
import { Location, useLocation } from 'react-router-dom'

import { useSessionLanguage } from '../global/context/SessionSettingsContext'
import { AuthUser } from '../model/User'
import { useNavigate } from '../navigation/custom-react-router-dom'
import { useAccountWriteContext } from './AccountContextContext'
import { useLocallyPersistedState } from './useStorage'

export type MaybeAuthSession = AuthUser | null
export type AuthSessionContextType = [MaybeAuthSession, Dispatch<SetStateAction<MaybeAuthSession>>]

export const AuthSessionContext = React.createContext<AuthSessionContextType>([null, () => {}])

export const initiateAuthSession = (location: Location): MaybeAuthSession => {
  const auth = localStorage.getItem('auth')

  if (
    auth &&
    location.pathname !== 'login' &&
    location.pathname !== '/sign-up/personal-details/verify-email' &&
    location.pathname !== '/sign-up/personal-details/verify-phone' &&
    !location.pathname.includes('reset-password')
  ) {
    return JSON.parse(auth)
  }

  return null
}

export function AuthSessionProvider({ children }: PropsWithChildren): JSX.Element {
  const location = useLocation()
  const navigate = useNavigate()
  const { refreshAccount } = useAccountWriteContext()
  const locale = useSessionLanguage()
  const firstUpdate = useRef(true)

  const [authSession, setAuthSession] = useLocallyPersistedState<MaybeAuthSession>(
    'auth',
    initiateAuthSession(location)
  )

  useLayoutEffect(() => {
    if (authSession && location.pathname.includes('logout') && !firstUpdate.current) {
      navigate('/')
    }
    firstUpdate.current = false
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authSession])

  useEffect(() => {
    if (authSession) {
      refreshAccount(locale)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authSession])

  const contextValue = useMemo<AuthSessionContextType>(
    () => [authSession, setAuthSession],
    [authSession, setAuthSession]
  )

  return <AuthSessionContext.Provider value={contextValue}>{children}</AuthSessionContext.Provider>
}
